import { useState, useEffect } from "react";
import { Trash2, Pencil, ArrowUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface Subscriber {
  id: string;
  email: string;
  source: string;
  notes: string | null;
  created_at: string;
}

type SortField = "created_at" | "source";
type SortDir = "asc" | "desc";

const SOURCE_COLORS: Record<string, string> = {
  "Japan Waitlist": "bg-destructive/15 text-destructive",
  Podcast_Launch: "bg-accent/15 text-accent",
  "Adventure List": "bg-green-500/15 text-green-700 dark:text-green-400",
  "Book Waitlist": "bg-blue-500/15 text-blue-700 dark:text-blue-400",
  General: "bg-muted text-muted-foreground",
};

const getSourceClass = (source: string) =>
  SOURCE_COLORS[source] ?? SOURCE_COLORS.General;

const SubscribersViewer = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Notes modal state
  const [editingSub, setEditingSub] = useState<Subscriber | null>(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  useEffect(() => {
    fetchSubscribers();
  }, [sortField, sortDir]);

  const fetchSubscribers = async () => {
    try {
      const { data, error } = await supabase
        .from("newsletter_subscribers")
        .select("*")
        .order(sortField, { ascending: sortDir === "asc" });

      if (error) throw error;
      setSubscribers(data || []);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      toast.error("Failed to load subscribers");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this subscriber?")) return;
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .delete()
        .eq("id", id);
      if (error) throw error;
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      toast.success("Subscriber deleted");
    } catch {
      toast.error("Failed to delete subscriber");
    }
  };

  const openNotes = (sub: Subscriber) => {
    setEditingSub(sub);
    setNotesDraft(sub.notes ?? "");
  };

  const saveNote = async () => {
    if (!editingSub) return;
    setIsSavingNote(true);
    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .update({ notes: notesDraft || null })
        .eq("id", editingSub.id);
      if (error) throw error;
      setSubscribers((prev) =>
        prev.map((s) =>
          s.id === editingSub.id ? { ...s, notes: notesDraft || null } : s
        )
      );
      toast.success("Note saved");
      setEditingSub(null);
    } catch {
      toast.error("Failed to save note");
    } finally {
      setIsSavingNote(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center gap-1 font-typewriter text-sm text-primary hover:text-accent transition-colors"
    >
      {label}
      <ArrowUpDown className="w-3 h-3" />
      {sortField === field && (
        <span className="text-xs text-muted-foreground">
          {sortDir === "asc" ? "↑" : "↓"}
        </span>
      )}
    </button>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-typewriter text-2xl font-bold text-primary">
          Subscribers
        </h2>
        <span className="text-sm text-muted-foreground">
          {subscribers.length} total
        </span>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading subscribers...</p>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <p className="text-muted-foreground">No subscribers yet</p>
        </div>
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-typewriter text-sm text-primary">
                  Email Address
                </th>
                <th className="text-left px-4 py-3">
                  <SortButton field="source" label="Source" />
                </th>
                <th className="text-left px-4 py-3">
                  <SortButton field="created_at" label="Date Signed Up" />
                </th>
                <th className="text-left px-4 py-3 font-typewriter text-sm text-primary">
                  Notes
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub) => (
                <tr key={sub.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-sm">{sub.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${getSourceClass(sub.source)}`}
                    >
                      {sub.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(sub.created_at)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground truncate max-w-[200px]">
                    {sub.notes || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openNotes(sub)}
                        title="Edit notes"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(sub.id)}
                        className="text-destructive hover:text-destructive"
                        title="Delete subscriber"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes Modal */}
      <Dialog open={!!editingSub} onOpenChange={(open) => !open && setEditingSub(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-typewriter">
              Notes — {editingSub?.email}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="Add notes about this subscriber…"
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSub(null)}>
              Cancel
            </Button>
            <Button onClick={saveNote} disabled={isSavingNote}>
              {isSavingNote ? "Saving…" : "Save Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscribersViewer;
